import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Bot, Send, X, Minus, Loader2, MonitorSmartphone, Crosshair } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { streamAssistant, cleanText, pickHighlights, highlightElement } from "./assistantStream";

const WELCOME = "Olá! 👋 Sou o Acompanha IA, seu assistente virtual de treinamento. Estou aqui para ajudar você a aprender a utilizar as ferramentas da plataforma Acompanha RJ. Posso explicar cada procedimento passo a passo, de maneira simples e prática. O que você gostaria de aprender hoje?";
const SUGGESTIONS = {
  PROFESSOR: ["Como registro uma aula e faço a chamada?", "Como lanço as notas do trimestre?", "Como imprimo o diário?", "Como registro uma ocorrência disciplinar?"],
  COZINHEIRA: ["Como lanço o consumo do dia?", "Como registro uma perda no estoque?", "Como altero o cardápio?"],
  GESTAO: ["Como cadastro a biometria de um aluno?", "Como abro o quiosque de presença?", "Como reabro um diário fechado?", "Como faço a busca ativa de alunos faltosos?"],
};
const newSession = () => `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

function Message({ m, onHighlight }) {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${mine ? "rounded-br-sm bg-blue-700 text-white" : "rounded-bl-sm border border-slate-200 bg-white text-slate-700"}`}
        data-testid={mine ? "ai-msg-user" : "ai-msg-assistant"}>
        <div className="whitespace-pre-wrap break-words">{cleanText(m.text) || (m.pending ? "…" : "")}</div>
        {m.highlights?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {m.highlights.map((h) => (
              <button key={h.testid} type="button" onClick={() => onHighlight(h.testid)} data-testid={`ai-highlight-${h.testid}`}
                className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-700 transition-colors hover:bg-orange-100">
                <Crosshair className="h-3 w-3" />Mostrar na tela: {h.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function AcompanhaIA() {
  const { user } = useAuth();
  const location = useLocation();
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const session = useRef(newSession());
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/assistant/status").then(({ data }) => setEnabled(data.enabled)).catch(() => setEnabled(false));
  }, [user?.id, location.pathname]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs]);

  if (!enabled || !user) return null;
  const role = user.role;
  const suggestions = SUGGESTIONS[role] || SUGGESTIONS.GESTAO;

  const onHighlight = (id) => {
    if (!highlightElement(id)) toast.info("Esse elemento não está visível agora. Siga os passos para chegar até ele.");
  };

  const ask = async (message, mode = "chat") => {
    if (busy || (!message.trim() && mode === "chat")) return;
    const pageTitle = document.querySelector("main h1")?.textContent?.trim()?.slice(0, 120) || "";
    const shown = mode === "screen" ? "Me ensine a usar esta tela" : message.trim();
    setMsgs((p) => [...p, { role: "user", text: shown }, { role: "assistant", text: "", pending: true }]);
    setText("");
    setBusy(true);
    let allowed = [];
    let acc = "";
    const patch = (fn) => setMsgs((p) => p.map((m, i) => (i === p.length - 1 ? fn(m) : m)));
    try {
      await streamAssistant({ session_id: session.current, message: shown, route: location.pathname, page_title: pageTitle, mode }, (ev) => {
        if (ev.t === "meta") allowed = ev.highlights || [];
        if (ev.t === "delta") { acc += ev.c; patch((m) => ({ ...m, text: acc })); }
        if (ev.t === "error") { acc = ev.c; patch((m) => ({ ...m, text: acc })); }
      });
      patch((m) => ({ ...m, pending: false, highlights: pickHighlights(acc, allowed) }));
    } catch (e) {
      patch((m) => ({ ...m, pending: false, text: e.message }));
      if (/desativado/.test(e.message)) { setOpen(false); setEnabled(false); }
    } finally {
      setBusy(false);
    }
  };

  const close = () => { setOpen(false); setMsgs([]); session.current = newSession(); };

  return (
    <>
      {!open && (
        <button type="button" onClick={() => setOpen(true)} data-testid="ai-assistant-fab"
          className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-full bg-blue-700 py-3 pl-3 pr-3 text-white shadow-lg shadow-blue-900/25 transition-transform hover:-translate-y-0.5 hover:bg-blue-800 sm:pr-5">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-500"><Bot className="h-5 w-5" /></span>
          <span className="hidden text-sm font-bold sm:inline">Acompanha IA</span>
        </button>
      )}
      {open && (
        <section data-testid="ai-assistant-panel" aria-label="Acompanha IA"
          className="fixed inset-x-2 bottom-2 z-50 flex h-[min(600px,calc(100dvh-1rem))] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-2xl sm:inset-x-auto sm:bottom-4 sm:right-4 sm:w-[400px]">
          <header className="flex items-center gap-3 bg-slate-900 px-4 py-3 text-white">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-500"><Bot className="h-5 w-5" /></span>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-bold">Acompanha IA</h2>
              <p className="text-xs text-slate-300">Seu assistente de treinamento</p>
            </div>
            <button type="button" onClick={() => setOpen(false)} title="Minimizar" data-testid="ai-assistant-minimize" className="rounded-lg p-1.5 transition-colors hover:bg-white/10"><Minus className="h-4 w-4" /></button>
            <button type="button" onClick={close} title="Fechar e limpar a conversa" data-testid="ai-assistant-close" className="rounded-lg p-1.5 transition-colors hover:bg-white/10"><X className="h-4 w-4" /></button>
          </header>
          <div className="flex-1 space-y-3 overflow-y-auto px-3 py-4" data-testid="ai-assistant-messages">
            <Message m={{ role: "assistant", text: WELCOME }} onHighlight={onHighlight} />
            {msgs.length === 0 && (
              <div className="flex flex-col gap-1.5 pl-1" data-testid="ai-assistant-suggestions">
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Perguntas frequentes</p>
                {suggestions.map((s, i) => (
                  <button key={s} type="button" onClick={() => ask(s)} data-testid={`ai-suggestion-${i}`}
                    className="w-fit rounded-full border border-blue-200 bg-white px-3 py-1.5 text-left text-xs font-medium text-blue-800 transition-colors hover:bg-blue-50">{s}</button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => <Message key={i} m={m} onHighlight={onHighlight} />)}
            <div ref={endRef} />
          </div>
          <div className="border-t border-slate-200 bg-white p-2.5">
            <button type="button" disabled={busy} onClick={() => ask("", "screen")} data-testid="ai-assistant-screen-help"
              className="mb-2 flex w-full items-center justify-center gap-1.5 rounded-lg bg-orange-50 py-1.5 text-xs font-semibold text-orange-700 transition-colors hover:bg-orange-100 disabled:opacity-50">
              <MonitorSmartphone className="h-3.5 w-3.5" />Me ensine a usar esta tela
            </button>
            <form onSubmit={(e) => { e.preventDefault(); ask(text); }} className="flex items-end gap-2">
              <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 1000))} rows={1} placeholder="Digite sua dúvida..."
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(text); } }}
                data-testid="ai-assistant-input"
                className="max-h-28 min-h-[40px] flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
              <button type="submit" disabled={busy || !text.trim()} data-testid="ai-assistant-send" title="Enviar"
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-700 text-white transition-colors hover:bg-blue-800 disabled:opacity-40">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </form>
            <p className="mt-1.5 text-center text-[10px] text-slate-400">O Acompanha IA apenas ensina; ele não faz alterações no sistema.</p>
          </div>
        </section>
      )}
    </>
  );
}
